import { Clock, ArrowUpRight } from 'lucide-react';
import { BlogPost } from '../../context/BlogContext';
import { getImageUrl } from '../../utils/imageUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

interface BlogCardProps {
  post: BlogPost;
  clickable?: boolean;
}

const BlogCard = ({ post, clickable = false }: BlogCardProps) => {
  const { title, excerpt, date, image, readTime, categories } = post;

  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  return (
    <div
      className={`group relative card overflow-hidden h-full ${
        clickable ? 'cursor-pointer card-hover' : ''
      }`}
    >
      {/* Gradient border effect on hover */}
      <div className="absolute inset-0 rounded-xl p-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgb(var(--color-primary) / 0.5), transparent 50%, rgb(var(--color-accent) / 0.3))',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />

      {/* Image */}
      <div className="relative aspect-video overflow-hidden bg-card">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent z-10" />

        {/* Corner accent */}
        <div
          className="absolute top-0 right-0 z-10 opacity-0 group-hover:opacity-100 transition-all duration-500"
          style={{
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '0 40px 40px 0',
            borderColor: 'transparent rgb(var(--color-primary) / 0.3) transparent transparent',
          }}
        />

        <img
          src={getImageUrl(image)}
          alt={title}
          className="w-full h-full object-cover object-center transition-all duration-700 ease-out-expo group-hover:scale-105 img-grayscale"
        />
      </div>

      {/* Content */}
      <div className="relative p-5 space-y-4">
        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          {categories.slice(0, 2).map((category, index) => (
            <span
              key={index}
              className="text-[10px] uppercase tracking-widest text-primary font-heading px-2 py-1 bg-primary/10 rounded"
            >
              {category}
            </span>
          ))}
        </div>

        {/* Title */}
        <div className="flex justify-between items-start gap-3">
          <h3 className="font-heading font-bold text-lg text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-2">
            {title}
          </h3>
          {clickable && (
            <span className="text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
              <ArrowUpRight className="w-5 h-5" />
            </span>
          )}
        </div>

        {/* Excerpt */}
        <div className="text-muted text-sm line-clamp-2 leading-relaxed">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              p: ({ children }) => <span>{children}</span>,
            }}
          >
            {excerpt}
          </ReactMarkdown>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-4 text-muted text-xs pt-2 border-t border-foreground/5">
          <span className="font-heading tracking-wide">{formattedDate}</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {readTime} min
          </span>
        </div>
      </div>
    </div>
  );
};

export default BlogCard;
